import React, { useState, useEffect } from 'react';
import { Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FaCheck, FaChevronLeft, FaChevronRight, FaChevronDown, FaChevronUp, FaUserSecret, FaUserCheck, FaPoll } from 'react-icons/fa';
import { db, auth } from '../../firebase/firebase-config';
import {
  doc,
  collection,
  addDoc,
  getDocs,
} from 'firebase/firestore';
import { useUser } from '../../context/UserContext';
import { exportPollToExcel } from './pollExcelExport';
import { markPollAsRead } from '../../services/pollsService';
import ContentDetailHero from '../../platform/ContentDetailHero';
import ContentUnitTag from '../common/ContentUnitTag';
import {
  isDetailHeroEnabled,
  PREMIUM_MODULE_IDS,
  premiumDetailClassName,
} from '../../platform/contentListVisual';
import './PollDetails.css';

function getQuestionHint(question) {
  switch (question.type) {
    case 'checkbox':
      return 'Izberite enega ali več odgovorov';
    case 'text':
      return 'Vpišite svoj odgovor';
    default:
      return 'Izberite en odgovor';
  }
}

function isQuestionAnswered(question, index, answers) {
  const ans = answers[index];
  if (question.type === 'checkbox') {
    return Array.isArray(ans) && ans.some((value) => String(value).trim());
  }
  if (question.type === 'text') {
    return typeof ans === 'string' && ans.trim().length > 0;
  }
  return ans != null && String(ans).trim().length > 0;
}

function getLikertRank(label) {
  const trimmed = String(label).trim();
  const onlyNumber = trimmed.match(/^(\d+)$/);
  if (onlyNumber) {
    return Number(onlyNumber[1]);
  }

  const leadingNumber = trimmed.match(/^(\d+)\s*[-–]/);
  if (leadingNumber) {
    return Number(leadingNumber[1]);
  }

  return null;
}

function isLikertScale(options) {
  if (!options.length) {
    return false;
  }

  return options.every((option) => getLikertRank(option) != null);
}

function buildChoiceResultRows(question, questionResults) {
  const results = questionResults || {};
  const officialOptions = (question.options || [])
    .map((option) => String(option || '').trim())
    .filter(Boolean);

  const officialSet = new Set(officialOptions);
  let rows = officialOptions.map((label) => ({
    label,
    count: results[label] || 0,
  }));

  if (isLikertScale(officialOptions)) {
    rows.sort((a, b) => getLikertRank(b.label) - getLikertRank(a.label));
  }

  const customRows = Object.keys(results)
    .map((key) => String(key).trim())
    .filter((label) => label && !officialSet.has(label))
    .map((label) => ({ label, count: results[label] || 0 }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'sl'));

  return [...rows, ...customRows];
}

function formatPollDate(date) {
  if (!date) return '';
  const value = date instanceof Date ? date : date?.toDate?.() || new Date(date);
  if (Number.isNaN(value.getTime?.())) return '';
  return value.toLocaleString('sl-SI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function PollVoteAcknowledgement({ variant = 'thanks', title, subtitle }) {
  return (
    <div className={`poll-vote-ack poll-vote-ack--${variant}`} role="status">
      <div className="poll-vote-ack__glow" aria-hidden="true" />
      <div className="poll-vote-ack__icon">
        <FaCheck aria-hidden="true" />
      </div>
      <div className="poll-vote-ack__body">
        <p className="poll-vote-ack__title">{title}</p>
        <p className="poll-vote-ack__subtitle">{subtitle}</p>
      </div>
    </div>
  );
}

function PollDetails({ poll, isAdmin, onEditPoll, onBackToList }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState({});
  const [textAnswers, setTextAnswers] = useState({});
  const [voters, setVoters] = useState([]);
  const [answersDocs, setAnswersDocs] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [userHasVoted, setUserHasVoted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showVoterList, setShowVoterList] = useState(false);

  const { user } = useUser();

  const canViewResults = poll.showResultsAfterSubmit || isAdmin;
  const canViewVoterList = isAdmin && poll.anonymous !== true;
  const canExportExcel = isAdmin;
  const isSingleQuestion = poll.questions.length === 1;
  /**
   * Fetch existing answers from Firestore => aggregate into
   * voteCounts (radio/checkbox) and textAnswers (free text).
   */
  const fetchData = async () => {
    setLoading(true);
    try {
      const pollRef = doc(db, 'polls', poll.id);
      const answersRef = collection(pollRef, 'answers');
      const querySnapshot = await getDocs(answersRef);

      const voteCounts = {};   // { [questionIndex]: { [optionValue]: number } }
      const freeTextMap = {};  // { [questionIndex]: string[] }
      const votersList = [];
      const docsData = [];

      let voted = false;

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        docsData.push(data);

        // Check if current user has voted
        if (data.userId === auth.currentUser.uid) {
          voted = true;
        }

        if (poll.anonymous !== true) {
          let fName = data.firstName || '';
          let lName = data.lastName || '';
          let voterName = data.displayName || 'Neznano';

          if (fName.trim() || lName.trim()) {
            voterName = `${fName} ${lName}`.trim();
          }

          votersList.push({
            displayName: voterName,
            userId: data.userId,
          });
        }

        // Aggregate answers
        data.answers.forEach((answerObj) => {
          const ansVal = answerObj.answer;
          // Rely on questionType in Firestore (fallback to poll definition if missing)
          const qType = answerObj.questionType
            || poll.questions[answerObj.questionIndex]?.type
            || 'radio';
          const qIndex = answerObj.questionIndex || 0;

          if (qType === 'text') {
            if (!freeTextMap[qIndex]) {
              freeTextMap[qIndex] = [];
            }
            if (Array.isArray(ansVal)) {
              ansVal.forEach((val) => {
                if (val.trim()) {
                  freeTextMap[qIndex].push(val);
                }
              });
            } else {
              if (ansVal.trim()) {
                freeTextMap[qIndex].push(ansVal);
              }
            }
          } else {
            if (!voteCounts[qIndex]) {
              voteCounts[qIndex] = {};
            }
            if (Array.isArray(ansVal)) {
              ansVal.forEach((val) => {
                voteCounts[qIndex][val] =
                  (voteCounts[qIndex][val] || 0) + 1;
              });
            } else {
              voteCounts[qIndex][ansVal] =
                (voteCounts[qIndex][ansVal] || 0) + 1;
            }
          }
        });
      });

      setResults(voteCounts);
      setTextAnswers(freeTextMap);
      setVoters(votersList);
      setUserHasVoted(voted);
      setAnswersDocs(docsData);
    } catch (error) {
      console.error('Error fetching poll data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    setShowVoterList(false);

    if (auth.currentUser?.uid) {
      markPollAsRead(auth.currentUser.uid, poll.id).catch((error) => {
        console.error('Error marking poll as read:', poll.id, error);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll.id]);

  const handleExportToExcel = async () => {
    try {
      await exportPollToExcel({
        poll,
        results,
        textAnswers,
        answersDocs,
      });
    } catch (error) {
      console.error('Error exporting poll to Excel:', error);
      alert('Izvoz v Excel ni uspel. Poskusite znova.');
    }
  };
  /**
   * Submit user answers:
   *  - For each question => store questionIndex + questionType + answer
   */
  const handleSubmit = async () => {
    // If user left any question unanswered, block
    if (Object.keys(answers).length < poll.questions.length) {
      alert('Prosimo odgovorite na vsa vprašanja.');
      return;
    }

    if (!userHasVoted && poll.active) {
      setLoading(true);
      const pollRef = doc(db, 'polls', poll.id);
      const answersRef = collection(pollRef, 'answers');

      // Store questionIndex and questionType
      const voteData = {
        answers: Object.entries(answers).map(([index, answer]) => {
          const questionIndex = Number(index);
          const questionType = poll.questions[questionIndex]?.type || 'radio';
          return {
            questionIndex,
            questionType,
            answer
          };
        }),
        createdAt: new Date(),
        userId: auth.currentUser.uid,
      };

      if (poll.anonymous !== true) {
        voteData.firstName = user.firstName || '';
        voteData.lastName = user.lastName || '';
        voteData.displayName = user.displayName || 'Neznano';
      }
      try {
        await addDoc(answersRef, voteData);
        setUserHasVoted(true);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 3000);
        if (auth.currentUser?.uid) {
          await markPollAsRead(auth.currentUser.uid, poll.id);
        }
        fetchData(); // Refresh data
      } catch (error) {
        console.error('Error submitting vote:', error);
        alert('Glasovanje ni uspelo. Poskusite znova.');
      }
      setLoading(false);
    }
  };

  // Pagination logic: show one question at a time
  const currentQuestion = poll.questions[currentPage] || {};

  const handleAnswerChange = (index, value) => {
    setAnswers({ ...answers, [index]: value });
  };

  const handleCheckboxChange = (index, value, checked) => {
    const currentAnswers = answers[index] || [];
    if (checked) {
      handleAnswerChange(index, [...currentAnswers, value]);
    } else {
      handleAnswerChange(
        index,
        currentAnswers.filter((a) => a !== value)
      );
    }
  };

  /**
   * Render a single question's form controls as selectable cards
   */
  const renderQuestion = (question, index) => {
    const disabled = userHasVoted || !poll.active;

    switch (question.type) {
      case 'radio':
        return (
          <div className="poll-vote-options">
            {question.options.map((option, oIndex) => {
              const isSelected = answers[index] === option;
              return (
                <button
                  type="button"
                  key={oIndex}
                  className={`poll-vote-option${isSelected ? ' poll-vote-option--selected' : ''}`}
                  onClick={() => handleAnswerChange(index, option)}
                  disabled={disabled}
                >
                  <span className="poll-vote-option__indicator" aria-hidden="true">
                    {isSelected && <FaCheck />}
                  </span>
                  <span className="poll-vote-option__label">{option}</span>
                </button>
              );
            })}
            {question.allowCustomAnswer && (
              <div
                className={`poll-vote-option poll-vote-option--custom${
                  answers[index] && !question.options.includes(answers[index])
                    ? ' poll-vote-option--selected'
                    : ''
                }`}
              >
                <div className="poll-vote-option__row">
                  <span className="poll-vote-option__indicator" aria-hidden="true">
                    {answers[index] && !question.options.includes(answers[index]) && (
                      <FaCheck />
                    )}
                  </span>
                  <span className="poll-vote-option__label">Drugo</span>
                </div>
                <Form.Control
                  type="text"
                  className="poll-vote-custom-input"
                  placeholder="Vpišite svoj odgovor..."
                  value={
                    answers[index] && !question.options.includes(answers[index])
                      ? answers[index]
                      : ''
                  }
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div className="poll-vote-options">
            {question.options.map((option, oIndex) => {
              const isSelected = answers[index]?.includes(option);
              return (
                <button
                  type="button"
                  key={oIndex}
                  className={`poll-vote-option poll-vote-option--checkbox${
                    isSelected ? ' poll-vote-option--selected' : ''
                  }`}
                  onClick={() => handleCheckboxChange(index, option, !isSelected)}
                  disabled={disabled}
                >
                  <span className="poll-vote-option__indicator" aria-hidden="true">
                    {isSelected && <FaCheck />}
                  </span>
                  <span className="poll-vote-option__label">{option}</span>
                </button>
              );
            })}
            {question.allowCustomAnswer && (
              <div className="poll-vote-option poll-vote-option--custom">
                <div className="poll-vote-option__row">
                  <span className="poll-vote-option__indicator" aria-hidden="true" />
                  <span className="poll-vote-option__label">Drugo</span>
                </div>
                <Form.Control
                  type="text"
                  className="poll-vote-custom-input"
                  placeholder="Vpišite svoj odgovor..."
                  value={
                    (answers[index] || []).find(
                      (value) => !question.options.includes(value)
                    ) || ''
                  }
                  onChange={(e) => {
                    const currentAnswers = answers[index] || [];
                    const officialOptions = question.options;
                    const newCustom = e.target.value;
                    const filtered = currentAnswers.filter((value) =>
                      officialOptions.includes(value)
                    );
                    handleAnswerChange(
                      index,
                      newCustom.trim() ? [...filtered, newCustom] : filtered
                    );
                  }}
                  disabled={disabled}
                />
              </div>
            )}
          </div>
        );

      case 'text':
        return (
          <Form.Control
            as="textarea"
            rows={3}
            className="poll-vote-text-input"
            placeholder="Vaš odgovor..."
            value={answers[index] || ''}
            onChange={(e) => handleAnswerChange(index, e.target.value)}
            disabled={disabled}
          />
        );

      default:
        return null;
    }
  };

  const currentQuestionAnswered = isQuestionAnswered(
    currentQuestion,
    currentPage,
    answers
  );
  const progressPercent = ((currentPage + 1) / poll.questions.length) * 100;
  const showVotingForm = !userHasVoted && poll.active;
  const showThankYou = userHasVoted || !poll.active;
  const useHero = isDetailHeroEnabled(PREMIUM_MODULE_IDS.POLLS);

  const pollHeroBadges = (
    <>
      <span
        className={`poll-vote-badge ${
          poll.active ? 'poll-vote-badge--active' : 'poll-vote-badge--inactive'
        }`}
      >
        {poll.active ? 'Aktivna' : 'Zaključena'}
      </span>
      <span className="poll-vote-badge poll-vote-badge--neutral">
        {poll.questions.length}{' '}
        {poll.questions.length === 1 ? 'vprašanje' : 'vprašanj'}
      </span>
      {poll.anonymous === true ? (
        <span className="poll-vote-badge poll-vote-badge--anonymous">
          Anonimna
        </span>
      ) : (
        <span className="poll-vote-badge poll-vote-badge--identified">
          Identificirana
        </span>
      )}
      <ContentUnitTag item={poll} />
      {(userHasVoted || poll.active) && (
        userHasVoted ? (
          <span className="poll-vote-badge poll-vote-badge--voted">
            Glas oddan
          </span>
        ) : (
          <span className="poll-vote-badge poll-vote-badge--cta">
            Glasuj
          </span>
        )
      )}
    </>
  );

  const pollHeroMeta = (
    <>
      <span>Ustvaril {poll.displayName || 'Anonimen'}</span>
      {poll.createdAt && (
        <>
          <span className="platform-content-detail-hero__meta-separator">·</span>
          <span>{formatPollDate(poll.createdAt)}</span>
        </>
      )}
      {poll.expires && (
        <>
          <span className="platform-content-detail-hero__meta-separator">·</span>
          <span>Poteče {formatPollDate(poll.expires)}</span>
        </>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="poll-details-loading">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Nalagam...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <article className={premiumDetailClassName(PREMIUM_MODULE_IDS.POLLS, 'poll-details platform-content-detail')}>
      <div className="poll-details-shell platform-content-detail-shell">
        {useHero ? (
          <ContentDetailHero
            title={poll.title}
            imageUrl={poll.thumbnailUrl}
            badges={pollHeroBadges}
            meta={pollHeroMeta}
          />
        ) : null}

        {showAlert && (
          <PollVoteAcknowledgement
            variant="submitted"
            title="Hvala za oddan glas!"
            subtitle="Vaš odgovor je bil uspešno shranjen."
          />
        )}

        {showThankYou && !showAlert && (
          <PollVoteAcknowledgement
            variant="thanks"
            title="Hvala za sodelovanje!"
            subtitle={
              poll.active
                ? 'Vaš glas je bil že oddan.'
                : 'Ta anketa je zaključena.'
            }
          />
        )}

        {showVotingForm && (
      <div className="poll-vote-card">
        {!useHero && (
        <div className="poll-vote-hero">
          <div className="poll-details-thumb">
            {poll.thumbnailUrl ? (
              <img src={poll.thumbnailUrl} alt="" />
            ) : (
              <div className="poll-details-thumb-placeholder" aria-hidden="true">
                <FaPoll />
              </div>
            )}
          </div>
          <div className="poll-vote-hero__text">
            <h1 className="poll-details-title">{poll.title}</h1>
            <div className="poll-vote-meta">
              <span
                className={`poll-vote-badge ${
                  poll.active ? 'poll-vote-badge--active' : 'poll-vote-badge--inactive'
                }`}
              >
                {poll.active ? 'Aktivna' : 'Zaključena'}
              </span>
              <span className="poll-vote-badge poll-vote-badge--neutral">
                {poll.questions.length}{' '}
                {poll.questions.length === 1 ? 'vprašanje' : 'vprašanj'}
              </span>
              {poll.anonymous === true ? (
                <span className="poll-vote-badge poll-vote-badge--anonymous">
                  Anonimna
                </span>
              ) : (
                <span className="poll-vote-badge poll-vote-badge--identified">
                  Identificirana
                </span>
              )}
              <ContentUnitTag item={poll} />
            </div>
            {(userHasVoted || poll.active) && (
              <div className="poll-vote-status">
                {userHasVoted ? (
                  <span className="poll-vote-badge poll-vote-badge--voted">
                    Glas oddan
                  </span>
                ) : (
                  <span className="poll-vote-badge poll-vote-badge--cta">
                    Glasuj
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {showVotingForm && (
          <>
            <div className="poll-vote-body">
              <div
                className={`poll-vote-privacy${
                  poll.anonymous === true
                    ? ' poll-vote-privacy--anonymous'
                    : ' poll-vote-privacy--identified'
                }`}
                role="note"
              >
                <span className="poll-vote-privacy__icon" aria-hidden="true">
                  {poll.anonymous === true ? <FaUserSecret /> : <FaUserCheck />}
                </span>
                <div className="poll-vote-privacy__text">
                  <p className="poll-vote-privacy__title">
                    {poll.anonymous === true
                      ? 'Anonimna anketa'
                      : 'Identificirana anketa'}
                  </p>
                  <p className="poll-vote-privacy__subtitle">
                    {poll.anonymous === true
                      ? 'Vaši odgovori niso povezani z vašim imenom. Organizator vidi le agregirane rezultate.'
                      : 'Vaši odgovori bodo povezani z vašim imenom. Organizator lahko vidi, kdo je glasoval.'}
                  </p>
                </div>
              </div>

              {!isSingleQuestion && (
                <div className="poll-vote-progress">
                  <div className="poll-vote-progress__label">
                    <span>Napredovanje</span>
                    <span className="poll-vote-progress__count">
                      {currentPage + 1} / {poll.questions.length}
                    </span>
                  </div>
                  <div className="poll-vote-progress__bar">
                    <div
                      className="poll-vote-progress__fill"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              )}
              <div className="poll-vote-question">
                <h2 className="poll-vote-question__title">
                  {currentQuestion.question}
                </h2>
                <p className="poll-vote-question__hint">
                  {getQuestionHint(currentQuestion)}
                </p>
                {renderQuestion(currentQuestion, currentPage)}
              </div>
            </div>

            <div
              className={`poll-vote-footer${
                isSingleQuestion ? ' poll-vote-footer--single' : ''
              }`}
            >
              {!isSingleQuestion && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 0}
                >
                  <FaChevronLeft aria-hidden="true" />
                  Nazaj
                </Button>
              )}
              {currentPage < poll.questions.length - 1 ? (                <Button
                  variant="primary"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!currentQuestionAnswered}
                >
                  Naprej
                  <FaChevronRight aria-hidden="true" />
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!currentQuestionAnswered}
                >
                  Potrdi
                  <FaCheck aria-hidden="true" />
                </Button>
              )}
            </div>
          </>
        )}
      </div>
        )}

      {canViewResults && (
        <>
          <div className="poll-results-card">
            <div className="poll-results-card__header">
              <h2 className="poll-results-card__title">Rezultati ankete</h2>
              {canExportExcel && (
                <Button variant="primary" size="sm" onClick={handleExportToExcel}>
                  Izvozi v Excel
                </Button>
              )}
            </div>
            <div className="poll-results-card__body">
              {poll.questions.map((question, qIndex) => {
                if (question.type === 'text') {
                  const theseTexts = textAnswers[qIndex] || [];
                  return (
                    <div key={qIndex} className="poll-results-question">
                      <h3 className="poll-results-question__title">
                        {question.question}
                      </h3>
                      {theseTexts.length === 0 ? (
                        <p className="poll-results-empty">Ni odgovorov.</p>
                      ) : (
                        <div className="poll-results-text-list">
                          {theseTexts.map((txt, i) => (
                            <div key={i} className="poll-results-text-item">
                              {txt}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                const questionResults = results[qIndex] || {};
                const resultRows = buildChoiceResultRows(question, questionResults);
                const totalVotes = resultRows.reduce((sum, row) => sum + row.count, 0);
                const maxCount = Math.max(...resultRows.map((row) => row.count), 1);

                return (
                  <div key={qIndex} className="poll-results-question">
                    <h3 className="poll-results-question__title">
                      {question.question}
                    </h3>
                    {resultRows.length === 0 ? (
                      <p className="poll-results-empty">Ni odgovorov.</p>
                    ) : (
                      <div className="poll-results-bars">
                        {resultRows.map(({ label, count }) => {
                          const widthPercent = (count / maxCount) * 100;
                          const sharePercent =
                            totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;

                          return (
                            <div key={label} className="poll-results-bar-row">
                              <div className="poll-results-bar-label">{label}</div>
                              <div className="poll-results-bar-visual">
                                <div className="poll-results-bar-track">
                                  <div
                                    className="poll-results-bar-fill"
                                    style={{ width: `${widthPercent}%` }}
                                  />
                                </div>
                                <div className="poll-results-bar-meta">
                                  <span className="poll-results-bar-count">{count}</span>
                                  <span className="poll-results-bar-percent">
                                    {sharePercent}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {canViewVoterList && (
            <div className="poll-results-card poll-results-card--voters">
              <button
                type="button"
                className="poll-voters-toggle"
                onClick={() => setShowVoterList((prev) => !prev)}
                aria-expanded={showVoterList}
              >
                <span className="poll-voters-toggle__text">
                  {showVoterList ? 'Skrij seznam glasovalcev' : 'Prikaži seznam glasovalcev'}
                  {!showVoterList && voters.length > 0 && (
                    <span className="poll-voters-toggle__count"> ({voters.length})</span>
                  )}
                </span>
                {showVoterList ? (
                  <FaChevronUp aria-hidden="true" />
                ) : (
                  <FaChevronDown aria-hidden="true" />
                )}
              </button>
              {showVoterList && (
                <div className="poll-results-card__body poll-results-card__body--voters">
                  <div className="poll-voters-list">
                    {voters.length > 0 ? (
                      voters.map((voter, index) => (
                        <div key={index} className="poll-voters-list__item">
                          {voter.displayName}
                        </div>
                      ))
                    ) : (
                      <p className="poll-results-empty">Ni glasovalcev.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {!canViewResults && userHasVoted && (
        <div className="poll-results-hidden">
          <h2 className="poll-results-card__title">Rezultati ankete</h2>
          <Alert variant="warning" className="mt-3 mb-3">
            Rezultati te ankete niso javno dostopni.
          </Alert>
          <Button variant="outline-secondary" onClick={onBackToList}>
            Nazaj na ankete
          </Button>
        </div>
      )}
      </div>
    </article>
  );
}

export default PollDetails;
